import * as React from "react";
import { mergeRefs } from "@sproutsocial/seeds-react-utilities";
import Icon from "@sproutsocial/seeds-react-icon";
import Container, { Accessory } from "./styles";
import type { TypeInputProps } from "./InputTypes";
import {
  InputContext,
  ClearButton,
  isClearButton,
  type TypeState,
} from "./shared";

/**
 * Styled-components implementation of Input. This is the legacy/SC path the
 * hybrid routes to when a consumer passes styled-system props or extends Input
 * via styled(). It is the verbatim implementation Input.tsx previously held.
 */
class InputStyledComponents extends React.Component<TypeInputProps, TypeState> {
  constructor(props: TypeInputProps) {
    super(props);
    this.state = {
      // Tracking hasValue in state allows us to hide ClearButton when there is no value to clear
      // for both controlled and uncontrolled inputs.
      hasValue: !!props.value,
    };
  }

  override componentDidUpdate({ value: prevValue }: TypeInputProps) {
    // Update hasValue state whenever props value changes
    const { value } = this.props;
    if (value !== prevValue) {
      this.updateState(value || "");
    }
  }

  static defaultProps = {
    autoFocus: false,
    disabled: false,
    type: "text",
    size: "default",
    appearance: "primary",
  };

  // Define our own ref for use in handleClear.
  // We use mergeRefs to pass both this.inputRef and this.props.innerRef to input.
  inputRef = React.createRef<HTMLInputElement>();

  handleBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    this.props.onBlur?.(e);

  handleClear = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    const input = this.inputRef.current;

    if (input) {
      // Clear the value via the input prototype, then dispatch an input event in order to trigger handleChange
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      )?.set;

      nativeInputValueSetter?.call(input, "");
      const inputEvent = new Event("input", {
        bubbles: true,
      });
      input.dispatchEvent(inputEvent);

      // call any onClear callback
      this.props.onClear?.(e);

      // wait for next cycle to update focus
      setTimeout(() => {
        input.focus();
      }, 0);
    }
  };

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange?.(e, e.currentTarget.value);
    // needed for uncontrolled inputs
    this.updateState(e.currentTarget.value);
  };

  handleFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    this.props.onFocus?.(e);

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    this.props.onKeyDown?.(e, e.currentTarget.value);

  handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) =>
    this.props.onKeyUp?.(e, e.currentTarget.value);

  handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) =>
    this.props.onPaste?.(e, e.currentTarget.value);

  updateState = (inputValue: string) => {
    const hasValue = inputValue !== "";
    const previousHasValue = this.state.hasValue;

    // Only update state if the value of `hasValue` has changed to avoid unnecessary renders.
    if (hasValue !== previousHasValue) {
      this.setState({
        hasValue,
      });
    }
  };

  override render() {
    const {
      autoComplete,
      autoFocus,
      disabled,
      readOnly,
      isInvalid,
      hasWarning,
      id,
      name,
      placeholder,
      type,
      required,
      value,
      elemBefore,
      elemAfter,
      maxLength,
      ariaLabel,
      ariaDescribedby,
      clearButtonLabel,
      innerRef = null,
      // These functions are used in the class functions above, but need to be extracted in order for `rest` to be correct
      /* eslint-disable @typescript-eslint/no-unused-vars */
      onBlur,
      onChange,
      onClear,
      onFocus,
      onKeyDown,
      onKeyUp,
      onPaste,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      inputProps = {},
      qa = {},
      appearance,
      size,
      ...rest
    } = this.props;

    // Convert autoComplete from a boolean prop to a string value.
    let autoCompleteValue: "on" | "off" | undefined;

    if (autoComplete !== undefined) {
      autoCompleteValue = autoComplete ? "on" : "off";
    }

    // Add default elemBefore and elemAfter elements if type is search.
    const elementBefore =
      type === "search" && !elemBefore ? (
        <Icon name="magnifying-glass-outline" aria-hidden color="icon.base" />
      ) : (
        elemBefore
      );

    // Do not add a ClearButton if an elemAfter prop was passed.
    const elementAfter =
      type === "search" && !elemAfter ? <ClearButton /> : elemAfter;

    return (
      <Container
        hasBeforeElement={!!elementBefore}
        hasAfterElement={!!elementAfter}
        disabled={disabled}
        invalid={!!isInvalid}
        warning={hasWarning}
        appearance={appearance}
        size={size}
        {...rest}
      >
        <InputContext.Provider
          value={{
            handleClear: this.handleClear,
            hasValue: this.state.hasValue,
            clearButtonLabel,
            size,
          }}
        >
          {elementBefore && <Accessory before>{elementBefore}</Accessory>}

          <input
            aria-invalid={!!isInvalid}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedby}
            aria-required={required}
            autoComplete={autoCompleteValue}
            autoFocus={autoFocus}
            disabled={disabled}
            readOnly={readOnly}
            id={id}
            name={name}
            placeholder={placeholder}
            type={type}
            required={required}
            value={value}
            maxLength={maxLength}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            onKeyUp={this.handleKeyUp}
            onPaste={this.handlePaste}
            ref={mergeRefs<HTMLInputElement>(innerRef, this.inputRef)}
            data-qa-input={name || ""}
            data-qa-input-isdisabled={disabled === true}
            data-qa-input-isrequired={required === true}
            {...qa}
            {...inputProps}
          />

          {elementAfter && (
            <Accessory after isClearButton={isClearButton(elementAfter)}>
              {elementAfter}
            </Accessory>
          )}
        </InputContext.Provider>
      </Container>
    );
  }
}

export default InputStyledComponents;
